import type { DataGridFilterState, DataGridSortingState, TableFilterState, TableSortingState } from '@ibsys/kit';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { ConversationListItem } from '@/entities/conversations';
import type { ExpertReportSettings } from '@/entities/settings';
import type { DialogFetchData, DialogMainColumn } from './types';

/** Части ключа кеша запроса списка: их смена перезапрашивает список */
type CacheKey = {
  /** Сортировка грида */
  gridSorting: DataGridSortingState;
  /** Фильтры грида */
  gridFilters: DataGridFilterState;
  /** Отбор раздела поверх пользовательского (выборка, которой ограничен список) */
  sectionFilters: TableFilterState;
  /** Видимые основные колонки */
  visibleMainColumns: DialogMainColumn[];
  /** Пути видимых атрибутов */
  visibleAttributePaths: string[];
  /** Параметры видимых экспертных заключений */
  expertReportSearchParams: ExpertReportSettings[];
  /** Активный режим поиска по содержимому */
  activeContentMode: string | null;
  /** Зафиксированное значение поиска по содержимому */
  committedContentValue: string;
  /** Ручной триггер перезагрузки */
  reloadKey: number;
};

/** Параметры ленивой постраничной загрузки списка диалогов */
type Params = {
  fetchData: DialogFetchData;
  /** Действующие фильтры выборки (с наложенным слоем перехода) */
  filters: TableFilterState;
  /** Действующая сортировка выборки */
  sorting: TableSortingState;
  /** Части ключа кеша запроса */
  cacheKey: CacheKey;
  /** Колбэк первой непустой загрузки — для инициализации зависящего от данных UI */
  onLoaded?: (data: ConversationListItem[]) => void;
};

/** Результат ленивой загрузки списка */
type Result = {
  /** Общее число строк выборки (до постраничности) */
  totalCount: number;
  /** Версия кеша запроса — передаётся гриду для перезапроса показанного диапазона */
  cacheVersion: string;
  /** Ключ пересоздания грида: меняется только когда список пуст */
  gridKey: string;
  /** Загрузка одной страницы для DataGrid lazy */
  fetchPage: (offset: number, limit: number, signal: AbortSignal) => Promise<ConversationListItem[]>;
};

/**
 * Ленивая постраничная загрузка списка диалогов для DataGrid.
 *
 * При смене параметров запроса сбрасываем `totalCount`: DataGrid lazy высчитывает limit как
 * min(pageSize, totalCount - offset) и при устаревшем `totalCount` (после применённого фильтра
 * с малым числом совпадений) отправляет нестандартный limit (вплоть до 1), который не попадает
 * в кеш TanStack Query, общий с дефолтным запросом limit=pageSize.
 *
 * Пока список пуст — пересоздаём грид (`gridKey`). Lazy-источник кита запрашивает первую страницу
 * только при монтировании (пока общее число строк неизвестно), а на смену `cacheVersion`
 * перезапрашивает лишь уже показанный диапазон строк. У пустого списка такого диапазона нет:
 * сброс кеша выбрасывает пришедший ответ, новый запрос не уходит и список залипает в загрузке
 * до перезагрузки страницы. С непустым списком диапазон известен — там грид сохраняем, чтобы
 * не терять позицию прокрутки и ширины колонок.
 */
export const useLazyPageFetch = ({ fetchData, filters, sorting, cacheKey, onLoaded }: Params): Result => {
  const {
    gridSorting,
    gridFilters,
    sectionFilters,
    visibleMainColumns,
    visibleAttributePaths,
    expertReportSearchParams,
    activeContentMode,
    committedContentValue,
    reloadKey,
  } = cacheKey;

  const cacheVersion = useMemo(
    () =>
      `${JSON.stringify(gridSorting)}|${JSON.stringify(gridFilters)}|${JSON.stringify(sectionFilters)}|${JSON.stringify(visibleMainColumns)}|${JSON.stringify(visibleAttributePaths)}|${JSON.stringify(expertReportSearchParams)}|${activeContentMode ?? ''}|${committedContentValue}|${reloadKey}`,
    [
      gridSorting,
      gridFilters,
      sectionFilters,
      visibleMainColumns,
      visibleAttributePaths,
      expertReportSearchParams,
      activeContentMode,
      committedContentValue,
      reloadKey,
    ],
  );

  const [totalCount, setTotalCount] = useState(0);

  const lastCacheVersionRef = useRef(cacheVersion);
  const gridKeyRef = useRef(cacheVersion);
  if (lastCacheVersionRef.current !== cacheVersion) {
    lastCacheVersionRef.current = cacheVersion;
    if (totalCount === 0) gridKeyRef.current = cacheVersion;
    setTotalCount(0);
  }

  const isLoadedRef = useRef(false);

  const fetchPage = useCallback(
    async (offset: number, limit: number, signal: AbortSignal) => {
      const result = await fetchData({ offset, limit, filters, sorting });
      // Запрос отменён (сменились параметры или грид размонтирован) — его ответ уже неактуален
      if (signal.aborted) return [];
      setTotalCount(result.total);
      if (result.items.length && !isLoadedRef.current) {
        isLoadedRef.current = true;
        onLoaded?.(result.items);
      }
      return result.items;
    },
    [fetchData, filters, sorting, onLoaded],
  );

  return { totalCount, cacheVersion, gridKey: gridKeyRef.current, fetchPage };
};
